import { Advisory } from "@ca.vmedia.tv/swagger-api";
import { Strings } from "@/locales/types/enums";
import { useI18n } from "vue-i18n";
import type { IRating, ITime } from "./types";
import { useNuxtApp } from '#app';
import { TimeUtils } from "@/utils";
import { type OnDemandCardData, type ProgramCardData, ComponentName } from "@/components/Cards/types";
import type { IEventSelected } from "@/components/RowView/interfaces/IEventSelected";
import type { IEventSelected as IMultiLineEventSelected } from "@/components/MultiLineView/types";

import { type ProgramGuideItem, type OnDemandItem } from "@ca.vmedia.tv/domains";

export const useParentControl = () => {
    const timeIntervalMs: number = 3600000;
    const { $domains, $services } = useNuxtApp();
    const { t } = useI18n();
    const ratings: Array<IRating> = [
        {title: t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_NO_RESTRICTION).toString(), id: Advisory.NR}, 
        {title: t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_G).toString(), id: Advisory.PG1},
        {title: t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_PG).toString(), id: Advisory.PG2},
        {title: t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_STRONGLY_CAUTIONED).toString(), id: Advisory.PG3},
        {title: t(Strings.SETTINGS_PARENT_CONTROL_RATINGS_RESTRICTED).toString(), id: Advisory.PG4}
    ];
    const unlockTimes: Array<ITime> = [
        {title: t(Strings.SETTINGS_PARENT_CONTROL_UNLOCK_TIMES_ASK_ALWAYS).toString(), type: 0, time: 0}, 
        {title: t(Strings.SETTINGS_PARENT_CONTROL_UNLOCK_TIMES_ONE_HOUR).toString(), type: 1, time: 0},
        {title: t(Strings.SETTINGS_PARENT_CONTROL_UNLOCK_TIMES_TWO_HOUR).toString(), type: 2, time: 0},
        {title: t(Strings.SETTINGS_PARENT_CONTROL_UNLOCK_TIMES_FOUR_HOUR).toString(), type: 4, time: 0}
    ];

    const getCurrentRating = (): IRating | undefined => {
        const itemLocalStorage = $domains.lsCacher.get('Rating');
        const rating = ratings.find(({ id }) => id === (itemLocalStorage ?? Advisory.NR));
        return rating;
    };
    const setCurrentRating = (id: Advisory | null = null): void => {
        if (id !== null) {
            $domains.lsCacher.put('Rating', id);
            return;
        }
        const rating = getCurrentRating();
        $domains.lsCacher.put('Rating', rating?.id ?? Advisory.NR);
    };

    const getCurrentTimes = (): ITime | null => {
        const itemLocalStorage = $domains.lsCacher.get<ITime>('Times');
        const time = unlockTimes.find(({ type }) => type === (itemLocalStorage?.type ?? 0));
        return time ? { ...time, ...itemLocalStorage } : itemLocalStorage;
    };
    const setCurrentTimes = (type: number | null = null): void => {
        if (type !== null) {
            const endTime = TimeUtils.dateNow() + timeIntervalMs * (type as number);
            $domains.lsCacher.put<ITime>('Times', { time: endTime, type });
            return;
        }
        const itemLocalStorage = $domains.lsCacher.get<ITime>('Times');
        if (!itemLocalStorage) {
            const endTime = TimeUtils.dateNow() + timeIntervalMs * (Advisory.PG2 as number);
            $domains.lsCacher.put<ITime>('Times', { time: endTime, type: Advisory.PG2 });
        } else {
            const endTime = TimeUtils.dateNow() + timeIntervalMs * (itemLocalStorage.type ?? 0);
            $domains.lsCacher.put<ITime>('Times', { time: endTime, type: itemLocalStorage.type });
        }
    };
    const needsUnlockPin = (advisory: number | null | undefined): boolean => {
        const currTime = getCurrentTimes();
        const currRating = getCurrentRating();
        if (currRating?.id === 0 || advisory === null || advisory === undefined) {
            return false;
        }
        return ((currTime?.time === 0 || (currTime !== null && TimeUtils.dateNow() >= currTime.time)) && advisory >= (currRating?.id ?? Advisory.NR)); 
    };
    
    const checkParentControlCard = async (event: IEventSelected | IMultiLineEventSelected): Promise<boolean> => {
        const componentName = event.componentName;
        const item = event.item;
        let advisory: Advisory | null | undefined = 0;
        let program: OnDemandItem | ProgramGuideItem | null = null;

        const alwaysTrueComponents = [
            ComponentName.CHANNEL,
            ComponentName.TABS_GENRES,
            ComponentName.FILTERS_VOD,
            ComponentName.CREDIT,
            ComponentName.PLUS,
            ComponentName.SHOW_ALL
        ];

        switch (componentName) {
            case ComponentName.CHANNEL_LIVE:
                program = await $services.guide.getChannelProgramByTime(Number(item.getId()), TimeUtils.dateNow());
                advisory = program?.advisory;
                break;

            case ComponentName.PROGRAM:
                program = (item as unknown as ProgramCardData).getProgram();
                advisory = program?.advisory;
                break;
    
            default:
                if (alwaysTrueComponents.some(name => name === componentName)) {
                    return true;
                }

                program = (item as unknown as OnDemandCardData).getPlayItem();
                advisory = program?.advisory;
                break;
        }

        return typeof advisory === 'number' ? !needsUnlockPin(advisory) : true;
    };

    return {
        ratings,
        unlockTimes,
        getCurrentRating,
        setCurrentRating,
        getCurrentTimes,
        setCurrentTimes,
        needsUnlockPin,
        checkParentControlCard
    }
}